import {
	isBankAccountNumberValid,
	isBillingAddressFilled,
	isRoutingNumberValid,
	isUsZipValid,
} from "../../utils/validators.ts";
import type {
	HostedFieldsEmpty,
	HostedFieldsValidity,
} from "./use-braintree.ts";

type NewPaymentValues = {
	cardholderName: string;
	bankName: string;
	bankAccountNumber: string;
	routingNumber: string;
	accountHolderName: string;
	billingAddress1: string;
	city: string;
	state: string;
	zip: string;
};

export type PaymentMethodFormErrors = {
	// CC
	cardholderName: boolean;
	cardNumberMissing: boolean;
	cardNumberInvalid: boolean;
	expirationDateMissing: boolean;
	expirationDateInvalid: boolean;
	cvvMissing: boolean;
	cvvInvalid: boolean;
	// ACH
	accountHolderName: boolean;
	bankName: boolean;
	bankAccountNumberMissing: boolean;
	bankAccountNumberInvalid: boolean;
	routingNumberMissing: boolean;
	routingNumberInvalid: boolean;
	// Billing
	billingAddress1: boolean;
	city: boolean;
	state: boolean;
	zipMissing: boolean;
	zipInvalid: boolean;
};

const EMPTY_ERRORS: PaymentMethodFormErrors = {
	cardholderName: false,
	cardNumberMissing: false,
	cardNumberInvalid: false,
	expirationDateMissing: false,
	expirationDateInvalid: false,
	cvvMissing: false,
	cvvInvalid: false,
	accountHolderName: false,
	bankName: false,
	bankAccountNumberMissing: false,
	bankAccountNumberInvalid: false,
	routingNumberMissing: false,
	routingNumberInvalid: false,
	billingAddress1: false,
	city: false,
	state: false,
	zipMissing: false,
	zipInvalid: false,
};

const isMissing = (value: string): boolean => value.trim() === "";

export type UsePaymentMethodFormValidationArgs = {
	newPaymentValues: NewPaymentValues;
	selectedMethod: "ACH" | "CreditCard" | null;
	hostedFieldsValidity: HostedFieldsValidity;
	hostedFieldsEmpty: HostedFieldsEmpty;
	showValidation: boolean;
};

export type UsePaymentMethodFormValidationResult = {
	isFormValid: boolean;
	errors: PaymentMethodFormErrors;
};

export const usePaymentMethodFormValidation = ({
	newPaymentValues,
	selectedMethod,
	hostedFieldsValidity,
	hostedFieldsEmpty,
	showValidation,
}: UsePaymentMethodFormValidationArgs): UsePaymentMethodFormValidationResult => {
	const billingValid =
		isBillingAddressFilled(newPaymentValues) &&
		isUsZipValid(newPaymentValues.zip);

	const isCcFormValid =
		newPaymentValues.cardholderName.trim() !== "" &&
		hostedFieldsValidity.number &&
		hostedFieldsValidity.expirationDate &&
		hostedFieldsValidity.cvv &&
		billingValid;

	const isAchFormValid =
		newPaymentValues.accountHolderName.trim() !== "" &&
		newPaymentValues.bankName.trim() !== "" &&
		isBankAccountNumberValid(newPaymentValues.bankAccountNumber) &&
		isRoutingNumberValid(newPaymentValues.routingNumber) &&
		billingValid;

	const isFormValid =
		selectedMethod === "CreditCard" ? isCcFormValid : isAchFormValid;

	if (!showValidation) {
		return { isFormValid, errors: EMPTY_ERRORS };
	}

	const routingNumberMissing = isMissing(newPaymentValues.routingNumber);
	const routingNumberInvalid =
		!routingNumberMissing &&
		!isRoutingNumberValid(newPaymentValues.routingNumber);

	const bankAccountNumberMissing = isMissing(
		newPaymentValues.bankAccountNumber,
	);
	const bankAccountNumberInvalid =
		!bankAccountNumberMissing &&
		!isBankAccountNumberValid(newPaymentValues.bankAccountNumber);

	const zipMissing = isMissing(newPaymentValues.zip);
	const zipInvalid = !zipMissing && !isUsZipValid(newPaymentValues.zip);

	const errors: PaymentMethodFormErrors = {
		cardholderName: isMissing(newPaymentValues.cardholderName),
		// Hosted-field errors stay visible until each field becomes valid.
		// "Missing" shows the "Required" message while empty; "Invalid" shows
		// a more specific message once the user has typed but the value is
		// not yet valid. Save-button gating uses isFormValid above.
		cardNumberMissing: hostedFieldsEmpty.number,
		cardNumberInvalid:
			!hostedFieldsEmpty.number && !hostedFieldsValidity.number,
		expirationDateMissing: hostedFieldsEmpty.expirationDate,
		expirationDateInvalid:
			!hostedFieldsEmpty.expirationDate && !hostedFieldsValidity.expirationDate,
		cvvMissing: hostedFieldsEmpty.cvv,
		cvvInvalid: !hostedFieldsEmpty.cvv && !hostedFieldsValidity.cvv,
		accountHolderName: isMissing(newPaymentValues.accountHolderName),
		bankName: isMissing(newPaymentValues.bankName),
		bankAccountNumberMissing,
		bankAccountNumberInvalid,
		routingNumberMissing,
		routingNumberInvalid,
		billingAddress1: isMissing(newPaymentValues.billingAddress1),
		city: isMissing(newPaymentValues.city),
		state: isMissing(newPaymentValues.state),
		zipMissing,
		zipInvalid,
	};

	return { isFormValid, errors };
};
